import posthog from "posthog-js";

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

export const isGAEnabled = !!MEASUREMENT_ID;

// If GA is expected to be active but the bootstrap script never ran (e.g. the
// guard in index.html fired incorrectly, or an ad-blocker stripped the tag),
// emit a warning so a broken setup is immediately visible in devtools.
if (isGAEnabled) {
  window.addEventListener("load", () => {
    if (typeof window.gtag !== "function") {
      console.warn(
        "[Analytics] GA bootstrap failed: VITE_GA_MEASUREMENT_ID is set but " +
          "window.gtag is not defined after page load. Check the inline script " +
          "in index.html or network requests to googletagmanager.com."
      );
    }
  });
}

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST as string | undefined;

// PostHog runs *alongside* GA4 for richer product analytics + session replay.
// It stays fully disabled (every call below becomes a no-op) unless both a
// project API key and host are configured — mirroring the GA4 guard above so an
// unconfigured deploy behaves exactly as before, with no errors.
export const isPostHogEnabled = !!(POSTHOG_KEY && POSTHOG_HOST);

if (isPostHogEnabled) {
  posthog.init(POSTHOG_KEY as string, {
    api_host: POSTHOG_HOST,
    // Autocapture clicks, inputs, and form submissions automatically.
    autocapture: true,
    // Capture pageviews automatically, including SPA route changes driven by
    // the History API (wouter navigations) — no manual $pageview needed.
    capture_pageview: "history_change",
    capture_pageleave: true,
    // Session replay: record real sessions so staff can watch them in PostHog.
    // (Recording also requires session replay to be enabled in PostHog project
    // settings.) We keep PostHog's sensible default masking.
    disable_session_recording: false,
  });
}

export function trackPageView(path: string) {
  if (isGAEnabled && typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
    });
  }
  // PostHog captures pageviews automatically (capture_pageview: "history_change"),
  // so we deliberately don't fire a manual $pageview here to avoid double-counting.
}

export function trackEvent(eventName: string, params?: Record<string, unknown>) {
  if (isGAEnabled && typeof window.gtag === "function") {
    window.gtag("event", eventName, params ?? {});
  }
  if (isPostHogEnabled) {
    posthog.capture(eventName, params);
  }
}
